/**
 * Pre-sale quote funnel — pure filtering / counting / valuation for the quotes
 * dashboard (quote/order separation, Slice 4). Built on the `quoteLifecycle`
 * buckets; `now` is always injected so callers own the clock and tests stay
 * deterministic.
 *
 * GRADUATED quotes (CLIENT_CONFIRMED and beyond) have left the pre-sale funnel
 * onto the order surface — they match NO pill and contribute nothing.
 *
 * EXCEPT accepted-but-unpaid quotes (2026-07-03): the Orders list hides
 * AWAITING_DEPOSIT orders, so a confirmed quote whose deposit hasn't been met
 * would otherwise be visible NOWHERE. Until the deposit gate is crossed the
 * quote stays on this funnel as still-pending (ALIVE + the SENT pill); once
 * the deposit lands the order becomes visible in the Orders list and the quote
 * graduates for real.
 *
 * That state is NO LONGER something a customer can create (payment-gated
 * confirmation slice 3 removed the public acceptance flip, spec §3.6). It is
 * still reachable three ways, so `isAwaitingDeposit` is KEPT: legacy documents,
 * a below-deposit payment, and the audited ADMIN `adminConfirmQuote` for a
 * customer on invoice terms — which births its order at AWAITING_DEPOSIT by
 * design. `isAcceptedAwaitingDeposit` below covers the new, SEPARATE state:
 * accepted on the public link and STILL SENT.
 */
import type { QuoteListLike } from '../services/quoteService';
import { quoteLifecycleBucket, type QuoteLifecycleBucket } from '../../shared/quoteLifecycle';
import { evaluateDepositGate } from '../../shared/depositGate';

export type FunnelFilter = 'ALIVE' | 'DRAFT' | 'SENT' | 'ON_HOLD' | 'EXPIRED' | 'LOST' | 'ARCHIVED';

/**
 * Which statuses the dashboard fetches EAGERLY vs on demand (backlog #78, the
 * 2026-09-04 denormalise/paginate study). SUPERSEDED + LOST were 25% of the
 * collection's bytes on prod and render only under the Archived / Lost pills,
 * so they load on the first click of either pill. Everything else — the alive
 * buckets plus the graduated money statuses that `isAwaitingDeposit` /
 * `isOrphanedMoney` still need to see — loads on mount.
 *
 * The two sets must PARTITION the status union: a status in neither would
 * silently vanish from the dashboard. `_exhaustive` below makes that a compile
 * error; the runtime test pins the bucket mapping.
 */
export const EAGER_FUNNEL_STATUSES = [
  'DRAFT',
  'SENT',
  'ON_HOLD',
  'CLIENT_CONFIRMED',
  'PARTIALLY_PAID',
  'FULLY_PAID',
  'PAYMENT_RECEIVED',
] as const satisfies readonly QuoteListLike['status'][];

/** `REJECTED` is a legacy stored value (mapped to LOST on read) — it is not in
 *  the type but it IS on old documents, so it must ride the lazy fetch. */
export const ARCHIVED_FUNNEL_STATUSES = ['SUPERSEDED', 'LOST', 'REJECTED'] as const;

type CoveredStatus =
  | (typeof EAGER_FUNNEL_STATUSES)[number]
  | Exclude<(typeof ARCHIVED_FUNNEL_STATUSES)[number], 'REJECTED'>;
// Compile-time partition check: fails if the QuotePayload status union gains a
// value that neither set lists.
const _exhaustive: Exclude<QuoteListLike['status'], CoveredStatus> extends never ? true : never =
  true;
void _exhaustive;

/** Pills whose rows live in the lazily-loaded archived set. */
export const funnelFilterNeedsArchived = (filter: FunnelFilter): boolean =>
  filter === 'ARCHIVED' || filter === 'LOST';

/** Buckets the rep still actively works. EXPIRED is deliberately NOT here — an
 *  aged SENT quote drops out of the "alive" view into Archived/Expired. */
const ALIVE_BUCKETS = new Set<QuoteLifecycleBucket>(['DRAFT', 'SENT', 'ON_HOLD']);
/** Dead-but-kept buckets. */
const ARCHIVED_BUCKETS = new Set<QuoteLifecycleBucket>(['EXPIRED', 'LOST', 'SUPERSEDED']);

/** Money statuses that can exist BEFORE the deposit gate is met. FULLY_PAID /
 *  PAYMENT_RECEIVED are past it by definition (and legacy docs may lack a
 *  ledger), so they graduate unconditionally. */
const PRE_DEPOSIT_MONEY_STATUSES = new Set(['CLIENT_CONFIRMED', 'PARTIALLY_PAID']);

/** Accepted by the customer but the deposit hasn't been met yet — the order
 *  exists at AWAITING_DEPOSIT (hidden from the Orders list), so the quote is
 *  this engagement's only staff-visible surface. Uses the same shared
 *  deposit-gate rule as the money CFs so display can't desync from the gate. */
export const isAwaitingDeposit = (q: QuoteListLike): boolean => {
  if (!PRE_DEPOSIT_MONEY_STATUSES.has(q.status)) return false;
  return !evaluateDepositGate({
    ledger: q.paymentLedger,
    grandTotal: q.totals?.grandTotal ?? 0,
    depositPercent: q.depositPercent,
  }).ok;
};

/**
 * The customer accepted on the public link but has not paid — the quotation is
 * still `SENT` and still AMENDABLE (payment-gated confirmation slice 3, spec
 * §3.8). This is the state the old Accept flip destroyed: it froze the quote
 * into an order that did not exist.
 *
 * Keyed on `metadata.acceptedAt`, which only `recordQuoteCustomerIntent`'s
 * ACCEPT_OFFLINE intent writes. The "ticked the T&C box and went to the
 * gateway" case writes `termsAcceptedAt` ONLY and is deliberately NOT counted
 * here — abandoning at the checkout is not an acceptance, and chasing it as one
 * sends a rep after a decision nobody made.
 *
 * DISJOINT FROM `isAwaitingDeposit` BY STATUS, not by arithmetic: that one
 * fires on the graduated money statuses (CLIENT_CONFIRMED / PARTIALLY_PAID),
 * this one only on `SENT`. A customer who accepts offline and then pays below
 * the deposit lands PARTIALLY_PAID, leaves this predicate and is picked up by
 * that one — no gap and no overlap.
 *
 * NOT part of funnel VISIBILITY: a SENT quote is already visible under All +
 * Sent, so this is a display note only. Do not thread it into
 * `isFunnelVisible` / `matchesFunnelFilter` / `funnelCounts`.
 */
export const isAcceptedAwaitingDeposit = (q: QuoteListLike): boolean =>
  q.status === 'SENT' &&
  typeof q.metadata?.acceptedAt === 'string' &&
  q.metadata.acceptedAt.length > 0;

/**
 * The customer asked for a fresh quotation from the expired public page
 * (spec §3.12 item 4). A flag with no notification behind it by design — the
 * dashboard note IS the signal, which is what keeps the expired page from
 * being a dead end wearing a button.
 */
export const hasRequestedUpdate = (q: QuoteListLike): boolean =>
  typeof q.metadata?.updateRequestedAt === 'string' && q.metadata.updateRequestedAt.length > 0;

/** Statuses in which a quote can legitimately be holding money. Mirrors the
 *  server's PROMOTE_ONLY_STATUSES (functions/src/transitions/confirmQuoteStatus.ts)
 *  deliberately: these are exactly the statuses confirmQuote's promote-only
 *  self-heal can repair an orphan from. */
const MONEY_HOLDING_STATUSES = new Set([
  'CLIENT_CONFIRMED',
  'PARTIALLY_PAID',
  'FULLY_PAID',
  'PAYMENT_RECEIVED',
]);

/**
 * ORPHANED MONEY — the quote holds a real payment but has NO order.
 *
 * A broken state, not a workflow stage. It mirrors
 * `shouldPromoteOnPayment` (functions/src/transitions/promoteQuoteToOrder.ts):
 * money that SHOULD have created an order — deposit gate met, positive total —
 * on a quote that has none. Normally the payment that banks that money also
 * promotes the quote (payment-gated confirmation, spec 2026-08-05 §3.1), so
 * this is false; it fires only when the promotion FAILED after the ledger
 * committed, which without this predicate would graduate off the funnel and be
 * visible on no staff surface at all (the shape of the 11 stuck production
 * quotes this initiative exists to eliminate).
 *
 * THE DEPOSIT GATE IS LOAD-BEARING, not a redundant guard. This slice makes
 * `SENT` payable, so a customer paying LESS than the deposit lands
 * PARTIALLY_PAID with no order — money without an order, but entirely correct:
 * `shouldPromoteOnPayment` declined on purpose. That is `isAwaitingDeposit`'s
 * case, and mis-claiming it here would badge a healthy quote as a
 * money-integrity fault and offer to mint its order + OC BEFORE the deposit is
 * met — the premature document-minting this initiative exists to remove.
 * Gating on `.ok` makes the two predicates **disjoint by construction** (`ok`
 * vs `!ok` over the same shared helper), so they cannot overlap or desync.
 *
 * DISJOINT, BUT DELIBERATELY NOT EXHAUSTIVE — an earlier version of this
 * comment claimed "exact complements", which is wrong and would mislead the
 * next editor. `MONEY_HOLDING_STATUSES` ⊃ `PRE_DEPOSIT_MONEY_STATUSES` by
 * `{FULLY_PAID, PAYMENT_RECEIVED}`, so a quote in one of those two statuses,
 * with an order, below the gate satisfies NEITHER predicate. No reachable
 * instance was found (both statuses imply the gate was passed) and it is not a
 * regression — but the third region is real, so a future widening of either
 * status set must re-check coverage rather than assume the pair still tiles
 * the space. `deriveLifecycleNote` in QuotationsDashboard handles it by
 * returning null.
 *
 * Recovery is confirmQuote's promote-only self-heal (idempotent, safe to
 * re-run). This predicate's only job is to keep the row reachable until then.
 *
 * KNOWN, ACCEPTABLE IMPRECISION: the gate below does not filter quarantined
 * entries, so it counts money the server would exclude and can read a quote as
 * deposit-met on money nobody accepted. (The client `PaymentLedgerEntry` does
 * now carry `quarantined` — the filter is absent by choice, not for want of the
 * field.) That errs toward SHOWING the row — the safe direction for a
 * surface-the-breakage predicate. Do not "fix" it here.
 */
export const isOrphanedMoney = (q: QuoteListLike): boolean => {
  if (q.projectId) return false;
  if (!MONEY_HOLDING_STATUSES.has(q.status)) return false;
  const grandTotal = q.totals?.grandTotal ?? 0;
  // `> 0` rather than `<= 0`: fails closed on NaN too. Mirrors
  // shouldPromoteOnPayment, and stops a $0 quote (where the gate is trivially
  // satisfied by paying nothing) from reading as an orphan.
  if (!(grandTotal > 0)) return false;
  return evaluateDepositGate({
    ledger: q.paymentLedger,
    grandTotal,
    depositPercent: q.depositPercent,
  }).ok;
};

/** Does the quote appear anywhere on the dashboard funnel? Pre-sale buckets
 *  always do; graduated quotes only while awaiting their deposit, or while
 *  holding orphaned money. */
export const isFunnelVisible = (q: QuoteListLike, now: number): boolean =>
  quoteLifecycleBucket(q, now) !== 'GRADUATED' || isAwaitingDeposit(q) || isOrphanedMoney(q);

/** Does a quote belong under the given funnel pill? */
export const matchesFunnelFilter = (
  q: QuoteListLike,
  filter: FunnelFilter,
  now: number,
): boolean => {
  const bucket = quoteLifecycleBucket(q, now);
  if (bucket === 'GRADUATED') {
    // Accepted-but-unpaid — and orphaned money — ride the funnel as
    // still-pending: All + Sent. Both must be threaded through visibility,
    // this filter AND the counts, or the row renders but matches no pill and
    // appears in no count: visible nowhere, bug unfixed.
    return (
      (filter === 'ALIVE' || filter === 'SENT') && (isAwaitingDeposit(q) || isOrphanedMoney(q))
    );
  }
  switch (filter) {
    case 'ALIVE':
      return ALIVE_BUCKETS.has(bucket);
    case 'ARCHIVED':
      return ARCHIVED_BUCKETS.has(bucket);
    case 'DRAFT':
      return bucket === 'DRAFT';
    case 'SENT':
      return bucket === 'SENT';
    case 'ON_HOLD':
      return bucket === 'ON_HOLD';
    case 'EXPIRED':
      return bucket === 'EXPIRED';
    case 'LOST':
      return bucket === 'LOST';
  }
};

/** Count each non-graduated quote into the pills it matches. ALIVE / ARCHIVED
 *  are umbrellas; the rest are per-bucket. */
export const funnelCounts = (
  quotes: QuoteListLike[],
  now: number,
): Record<FunnelFilter, number> => {
  const counts: Record<FunnelFilter, number> = {
    ALIVE: 0,
    DRAFT: 0,
    SENT: 0,
    ON_HOLD: 0,
    EXPIRED: 0,
    LOST: 0,
    ARCHIVED: 0,
  };
  for (const q of quotes) {
    const bucket = quoteLifecycleBucket(q, now);
    if (bucket === 'GRADUATED') {
      if (isAwaitingDeposit(q) || isOrphanedMoney(q)) {
        counts.ALIVE++;
        counts.SENT++;
      }
      continue;
    }
    if (ALIVE_BUCKETS.has(bucket)) counts.ALIVE++;
    if (ARCHIVED_BUCKETS.has(bucket)) counts.ARCHIVED++;
    if (
      bucket === 'DRAFT' ||
      bucket === 'SENT' ||
      bucket === 'ON_HOLD' ||
      bucket === 'EXPIRED' ||
      bucket === 'LOST'
    ) {
      counts[bucket]++;
    }
  }
  return counts;
};

/** Headline pipeline value: ALIVE quotes (DRAFT/SENT/ON_HOLD + accepted-but-
 *  unpaid — excludes expired/lost/superseded/deposit-met), collapsed to the
 *  latest version per root chain so a re-issued quote counts once. Independent
 *  of the active pill.
 *
 *  Orphaned-money quotes are deliberately NOT added here, unlike the other three
 *  sites: this is pipeline still to be WON, and that money is already collected
 *  — counting it would inflate the headline with banked revenue. Consequence:
 *  such a row is counted in the ALIVE pill but contributes no value. */
export const funnelTotalValue = (quotes: QuoteListLike[], now: number): number => {
  const latestByRoot = new Map<string, QuoteListLike>();
  for (const q of quotes) {
    const bucket = quoteLifecycleBucket(q, now);
    const alive = ALIVE_BUCKETS.has(bucket) || (bucket === 'GRADUATED' && isAwaitingDeposit(q));
    if (!alive) continue;
    const root = q.metadata?.rootQuoteId ?? q.id;
    const existing = latestByRoot.get(root);
    if (!existing || (q.metadata?.version ?? 1) >= (existing.metadata?.version ?? 1)) {
      latestByRoot.set(root, q);
    }
  }
  let total = 0;
  for (const q of latestByRoot.values()) total += q.totals?.grandTotal ?? 0;
  return total;
};
